import { SessionActionResult, SessionType } from './session-enums';
import { Action, ActionType, DiscountSetting, DiscountTypeEnum } from './funnel-action';
import * as moment from 'moment-timezone';
import { BaseModel } from './base-model';
import { IconFile } from './icon-file';
import { AudioFile } from './audio-file';
import { ContactType } from './campaign';
import { SipRegion } from './sip';
import { Image, SignedURL } from './image';
import { StepButtonControlTypes, StepButtonShapes } from './steps';
import { CampaignProduct, getCampaignProductImageHtml } from './product';
import { PhoneCarrierType } from './phone';
import { formatMoney } from '../_helpers/functions';
import { SurveyStep } from '../offer-builder/survey/survey';
import {ContentBlockType, WidgetVariable} from './widget';

export enum FunnelInputTypeEnum {
  Choice = 0,
  Text = 1,
  Phone = 2,
  Item = 3,
  ActiveItem = 4,
  Product = 5,
  FAQs = 6,
  BillingAddress = 7,
  ShippingAddress = 8,
  ItemStatus = 9,
  InputActionStatus = 10,
  Radio = 11,
  Select = 12,
  NextSelectedItem = 13, //deprecated
  RadioSelect = 14,
  Hidden = 15,
  EnterProductFunnel = 16,
  PaymentInfo = 17,
  Link = 18,
  EnterTroubleshooter = 19,
  ContactCallCenter = 20,
  SelectCampaign = 21,
  MatchCustomer = 22,
  Date = 23,
  Checkout = 24,
  CheckoutConfirm = 25,
  RepeatActionHoldoff = 26,
  ShipmentTracking = 27,
  Confirmation = 28,
  ConfirmationNo = 29,
  CustomerPortalHome = 30,
  CustomerPortalOrders = 31,
  CustomerPortalSubscriptions = 32, // replaces retired CustomerPortalOrderHistory
  CustomerPortalSubscriptionDetail = 33, // replaces retired CustomerPortalPaymentInfo
  CustomerPortalPersonalInfo = 34,
  CustomerPortalOrderDetail = 35,
  RepeatActionHoldoffResponse = 36,
  SubscriptionItem = 37,

  // system child types
  ChangeQuantity = 1001,
  ChangeInterval = 1002,
  Cancel = 1003,
  NoActiveItems = 1004,
  ChangeProduct = 1005,
  Return = 1006,
  Billing = 1007,
  AltChildExit = 1008,
  PathNotFound = 1009,
  NoActiveCampaigns = 1010,
  CustomerNotFound = 1011,
  RebillItem = 1012,
  ShipmentReturnLabel = 1013,
  ShipmentReturnQRCode = 1014,
  PauseSubscription = 1015,
  SkipSubscription = 1016,
  ReactivateSubscription = 1017,
  CancelSubscription = 1018,
  CancelTrial = 1019,
  CancelSubscriptionHold = 1020,
  CancelTrialHold = 1021,
  ReturnSubscription = 1022,
  ReturnTrial = 1023,
  CallCenterClosed = 1024,
  NoChange = 1025,
  ChangeVariant = 1026,
  ChangeSubscriptionProduct = 1027,
  ChangeSubscriptionVariant = 1028,
  CancelRebillHold = 1029,
  OfferDetails = 1030,
  BillNow = 1031,
  ShippingProcessing = 1032,
  ReturnPending = 1033,
  ExtendTrial = 1034,
  AdjustTrialFrequency = 1035,
  BillTrialNow = 1036,
  Resolve = 1037,
  ResolveSubscription = 1038,
}

export let FunnelInputTypeLabels = {};
FunnelInputTypeLabels[FunnelInputTypeEnum.Choice] = 'Choice - Buttons';
FunnelInputTypeLabels[FunnelInputTypeEnum.Radio] = 'Choice - Radio Buttons';
FunnelInputTypeLabels[FunnelInputTypeEnum.Select] = 'Choice - Select Box';
FunnelInputTypeLabels[FunnelInputTypeEnum.Text] = 'Text Input';
FunnelInputTypeLabels[FunnelInputTypeEnum.Date] = 'Date';
FunnelInputTypeLabels[FunnelInputTypeEnum.Phone] = 'Phone Number';
FunnelInputTypeLabels[FunnelInputTypeEnum.Item] = 'Order Item Selection';
FunnelInputTypeLabels[FunnelInputTypeEnum.ActiveItem] = 'Active Order Item Selection';
FunnelInputTypeLabels[FunnelInputTypeEnum.SubscriptionItem] = 'Subscription Item Selection';
FunnelInputTypeLabels[FunnelInputTypeEnum.Product] = 'Product Info';
FunnelInputTypeLabels[FunnelInputTypeEnum.FAQs] = 'FAQs';
FunnelInputTypeLabels[FunnelInputTypeEnum.BillingAddress] = 'Update Billing Address';
FunnelInputTypeLabels[FunnelInputTypeEnum.ShippingAddress] = 'Update Shipping Address';
FunnelInputTypeLabels[FunnelInputTypeEnum.ItemStatus] = 'Order Item Status';
FunnelInputTypeLabels[FunnelInputTypeEnum.InputActionStatus] = 'Input Action Status';
FunnelInputTypeLabels[FunnelInputTypeEnum.ChangeQuantity] = 'Change Item Quantity';
FunnelInputTypeLabels[FunnelInputTypeEnum.ChangeInterval] = 'Change Item Interval';
FunnelInputTypeLabels[FunnelInputTypeEnum.Cancel] = 'Cancel Item';
FunnelInputTypeLabels[FunnelInputTypeEnum.NoActiveItems] = 'No Active Items';
FunnelInputTypeLabels[FunnelInputTypeEnum.ChangeProduct] = 'Change Product';
FunnelInputTypeLabels[FunnelInputTypeEnum.Return] = 'Return Item';
FunnelInputTypeLabels[FunnelInputTypeEnum.RadioSelect] = 'Choice - Radio for Desktop, Select for Mobile';
FunnelInputTypeLabels[FunnelInputTypeEnum.Hidden] = 'Hidden';
FunnelInputTypeLabels[FunnelInputTypeEnum.Billing] = 'Billing';
FunnelInputTypeLabels[FunnelInputTypeEnum.EnterProductFunnel] = 'Enter Product Path';
FunnelInputTypeLabels[FunnelInputTypeEnum.PaymentInfo] = 'Update Payment Info';
FunnelInputTypeLabels[FunnelInputTypeEnum.Link] = 'Choice - Link';
FunnelInputTypeLabels[FunnelInputTypeEnum.EnterTroubleshooter] = 'Enter Troubleshooter';
FunnelInputTypeLabels[FunnelInputTypeEnum.AltChildExit] = 'Alternate Child Exit';
FunnelInputTypeLabels[FunnelInputTypeEnum.PathNotFound] = 'Path Not Found';
FunnelInputTypeLabels[FunnelInputTypeEnum.ContactCallCenter] = 'Contact Call Center';
FunnelInputTypeLabels[FunnelInputTypeEnum.SelectCampaign] = 'Brand Selection';
FunnelInputTypeLabels[FunnelInputTypeEnum.NoActiveCampaigns] = 'No Active Brands';
FunnelInputTypeLabels[FunnelInputTypeEnum.MatchCustomer] = 'Match Customer';
FunnelInputTypeLabels[FunnelInputTypeEnum.CustomerNotFound] = 'Customer Not Found';
FunnelInputTypeLabels[FunnelInputTypeEnum.Checkout] = 'Checkout';
FunnelInputTypeLabels[FunnelInputTypeEnum.CheckoutConfirm] = 'Checkout Confirmation';
FunnelInputTypeLabels[FunnelInputTypeEnum.RebillItem] = 'Rebill Item Selection';
FunnelInputTypeLabels[FunnelInputTypeEnum.RepeatActionHoldoff] = 'Repeat Action Holdoff';
FunnelInputTypeLabels[FunnelInputTypeEnum.ShipmentTracking] = 'Shipment Tracking';
FunnelInputTypeLabels[FunnelInputTypeEnum.ShipmentReturnLabel] = 'Shipment Return Label';
FunnelInputTypeLabels[FunnelInputTypeEnum.Confirmation] = 'Confirmation';
FunnelInputTypeLabels[FunnelInputTypeEnum.ConfirmationNo] = 'Confirmation Declined';
FunnelInputTypeLabels[FunnelInputTypeEnum.ShipmentReturnQRCode] = 'Shipment Return QR Code';
FunnelInputTypeLabels[FunnelInputTypeEnum.CustomerPortalHome] = 'Customer Portal Home';
FunnelInputTypeLabels[FunnelInputTypeEnum.CustomerPortalOrders] = 'Customer Portal Orders List';
FunnelInputTypeLabels[FunnelInputTypeEnum.CustomerPortalSubscriptions] = 'Customer Portal Subscriptions List';
FunnelInputTypeLabels[FunnelInputTypeEnum.CustomerPortalSubscriptionDetail] = 'Customer Portal Subscription Detail';
FunnelInputTypeLabels[FunnelInputTypeEnum.CustomerPortalPersonalInfo] = 'Customer Portal Contact Information';
FunnelInputTypeLabels[FunnelInputTypeEnum.CustomerPortalOrderDetail] = 'Customer Portal Order Detail';
FunnelInputTypeLabels[FunnelInputTypeEnum.PauseSubscription] = 'Pause Subscription';
FunnelInputTypeLabels[FunnelInputTypeEnum.SkipSubscription] = 'Skip Subscription';
FunnelInputTypeLabels[FunnelInputTypeEnum.ReactivateSubscription] = 'Reactivate Subscription';
FunnelInputTypeLabels[FunnelInputTypeEnum.CancelSubscription] = 'Cancel Subscription';
FunnelInputTypeLabels[FunnelInputTypeEnum.CancelTrial] = 'Cancel Trial';
FunnelInputTypeLabels[FunnelInputTypeEnum.CancelSubscriptionHold] = 'Cancel Subscription on Hold';
FunnelInputTypeLabels[FunnelInputTypeEnum.CancelTrialHold] = 'Cancel Trial on Hold';
FunnelInputTypeLabels[FunnelInputTypeEnum.ReturnSubscription] = 'Return Order Subscription';
FunnelInputTypeLabels[FunnelInputTypeEnum.ReturnTrial] = 'Return Trial';
FunnelInputTypeLabels[FunnelInputTypeEnum.RepeatActionHoldoffResponse] = 'Repeat Action Holdoff Response';
FunnelInputTypeLabels[FunnelInputTypeEnum.CallCenterClosed] = 'Call Center Closed';
FunnelInputTypeLabels[FunnelInputTypeEnum.NoChange] = 'No Change';
FunnelInputTypeLabels[FunnelInputTypeEnum.ChangeVariant] = 'Change Item Variant';
FunnelInputTypeLabels[FunnelInputTypeEnum.ChangeSubscriptionProduct] = 'Change Subscription Product';
FunnelInputTypeLabels[FunnelInputTypeEnum.ChangeSubscriptionVariant] = 'Change Subscription Variant';
FunnelInputTypeLabels[FunnelInputTypeEnum.CancelRebillHold] = 'Cancel Rebill on Hold';
FunnelInputTypeLabels[FunnelInputTypeEnum.OfferDetails] = 'Offer Details';
FunnelInputTypeLabels[FunnelInputTypeEnum.BillNow] = 'Bill Now';
FunnelInputTypeLabels[FunnelInputTypeEnum.ShippingProcessing] = 'Shipping Processing';
FunnelInputTypeLabels[FunnelInputTypeEnum.ReturnPending] = 'Return Pending';
FunnelInputTypeLabels[FunnelInputTypeEnum.ExtendTrial] = 'Extend Trial';
FunnelInputTypeLabels[FunnelInputTypeEnum.AdjustTrialFrequency] = 'Adjust Trial Frequency';
FunnelInputTypeLabels[FunnelInputTypeEnum.BillTrialNow] = 'Bill Trial Now';

export const FunnelInputSystemTypes = [
  FunnelInputTypeEnum.Item,
  FunnelInputTypeEnum.ActiveItem,
  FunnelInputTypeEnum.SubscriptionItem,
  FunnelInputTypeEnum.Product,
  FunnelInputTypeEnum.FAQs,
  FunnelInputTypeEnum.BillingAddress,
  FunnelInputTypeEnum.ShippingAddress,
  FunnelInputTypeEnum.ItemStatus,
  FunnelInputTypeEnum.InputActionStatus,
  FunnelInputTypeEnum.Hidden,
  FunnelInputTypeEnum.EnterProductFunnel,
  FunnelInputTypeEnum.PaymentInfo,
  FunnelInputTypeEnum.EnterTroubleshooter,
  FunnelInputTypeEnum.ContactCallCenter,
  FunnelInputTypeEnum.SelectCampaign,
  FunnelInputTypeEnum.MatchCustomer,
  FunnelInputTypeEnum.Checkout,
  FunnelInputTypeEnum.CheckoutConfirm,
  FunnelInputTypeEnum.RebillItem,
  FunnelInputTypeEnum.RepeatActionHoldoff,
  FunnelInputTypeEnum.ShipmentTracking,
  FunnelInputTypeEnum.Confirmation,
  FunnelInputTypeEnum.ConfirmationNo,
  FunnelInputTypeEnum.NoChange,
  FunnelInputTypeEnum.CustomerPortalHome,
  FunnelInputTypeEnum.CustomerPortalOrders,
  FunnelInputTypeEnum.CustomerPortalSubscriptions,
  FunnelInputTypeEnum.CustomerPortalSubscriptionDetail,
  FunnelInputTypeEnum.CustomerPortalPersonalInfo,
  FunnelInputTypeEnum.CustomerPortalOrderDetail,
  FunnelInputTypeEnum.ChangeVariant,
  FunnelInputTypeEnum.ChangeSubscriptionProduct,
  FunnelInputTypeEnum.ChangeSubscriptionVariant,
];

export const FunnelInputCustomerPortalInputs = [
  FunnelInputTypeEnum.CustomerPortalHome,
  FunnelInputTypeEnum.CustomerPortalOrders,
  FunnelInputTypeEnum.CustomerPortalSubscriptions,
  FunnelInputTypeEnum.CustomerPortalPersonalInfo,
];

export const FunnelInputCustomerPortalTypes = [
  FunnelInputTypeEnum.CustomerPortalHome,
  FunnelInputTypeEnum.CustomerPortalOrders,
  FunnelInputTypeEnum.CustomerPortalSubscriptions,
  FunnelInputTypeEnum.CustomerPortalSubscriptionDetail,
  FunnelInputTypeEnum.CustomerPortalPersonalInfo,
  FunnelInputTypeEnum.CustomerPortalOrderDetail,
];

export const SessionStartStepTypes = [
  FunnelInputTypeEnum.Product,
  FunnelInputTypeEnum.Item,
  FunnelInputTypeEnum.ActiveItem,
  FunnelInputTypeEnum.SubscriptionItem,
  FunnelInputTypeEnum.FAQs,
  FunnelInputTypeEnum.BillingAddress,
  FunnelInputTypeEnum.ShippingAddress,
  FunnelInputTypeEnum.PaymentInfo,
  FunnelInputTypeEnum.ItemStatus,
  FunnelInputTypeEnum.EnterProductFunnel,
  FunnelInputTypeEnum.EnterTroubleshooter,
  FunnelInputTypeEnum.ContactCallCenter,
  FunnelInputTypeEnum.Checkout,
  FunnelInputTypeEnum.ShipmentTracking,
];

export const SessionStartCustomerPortalStepTypes = [...SessionStartStepTypes, ...FunnelInputCustomerPortalTypes];
export const SessionStartHybridStepTypes = [...SessionStartStepTypes, FunnelInputTypeEnum.SelectCampaign];

//system input types that allow child inputs
export let FunnelSystemInputChildTypes = {};
FunnelSystemInputChildTypes[FunnelInputTypeEnum.Item] = [
  FunnelInputTypeEnum.NoActiveItems,
  FunnelInputTypeEnum.ReactivateSubscription,
  FunnelInputTypeEnum.ReturnSubscription,
];
FunnelSystemInputChildTypes[FunnelInputTypeEnum.ActiveItem] = [
  FunnelInputTypeEnum.NoActiveItems,
  FunnelInputTypeEnum.ReturnSubscription,
];
FunnelSystemInputChildTypes[FunnelInputTypeEnum.SubscriptionItem] = [
  FunnelInputTypeEnum.NoActiveItems,
  FunnelInputTypeEnum.ReactivateSubscription,
];
FunnelSystemInputChildTypes[FunnelInputTypeEnum.ContactCallCenter] = [FunnelInputTypeEnum.CallCenterClosed];
FunnelSystemInputChildTypes[FunnelInputTypeEnum.RepeatActionHoldoff] = [FunnelInputTypeEnum.NoActiveItems];
FunnelSystemInputChildTypes[FunnelInputTypeEnum.SelectCampaign] = [FunnelInputTypeEnum.NoActiveCampaigns];
FunnelSystemInputChildTypes[FunnelInputTypeEnum.MatchCustomer] = [FunnelInputTypeEnum.CustomerNotFound];
FunnelSystemInputChildTypes[FunnelInputTypeEnum.ItemStatus] = [
  FunnelInputTypeEnum.ChangeQuantity,
  FunnelInputTypeEnum.ChangeInterval,
  FunnelInputTypeEnum.Cancel,
  FunnelInputTypeEnum.ChangeProduct,
  FunnelInputTypeEnum.ChangeVariant,
  FunnelInputTypeEnum.ChangeSubscriptionProduct,
  FunnelInputTypeEnum.ChangeSubscriptionVariant,
  FunnelInputTypeEnum.Return,
  FunnelInputTypeEnum.Billing,
  FunnelInputTypeEnum.ShipmentReturnLabel,
  FunnelInputTypeEnum.ShipmentReturnQRCode,
];
FunnelSystemInputChildTypes[FunnelInputTypeEnum.EnterProductFunnel] = [
  FunnelInputTypeEnum.AltChildExit,
  FunnelInputTypeEnum.PathNotFound,
];
FunnelSystemInputChildTypes[FunnelInputTypeEnum.EnterTroubleshooter] = [
  FunnelInputTypeEnum.AltChildExit,
  FunnelInputTypeEnum.PathNotFound,
];
FunnelSystemInputChildTypes[FunnelInputTypeEnum.InputActionStatus] = [
  FunnelInputTypeEnum.ShipmentReturnLabel,
  FunnelInputTypeEnum.ShipmentReturnQRCode,
];
FunnelSystemInputChildTypes[FunnelInputTypeEnum.Confirmation] = [FunnelInputTypeEnum.ConfirmationNo];
FunnelSystemInputChildTypes[FunnelInputTypeEnum.PaymentInfo] = [];

export const FunnelInputDefaultActions = {
  [FunnelInputTypeEnum.Checkout]: ActionType.Checkout,
};

export enum OfferIntents {
  Legacy = 0,
  CancelOrder = 1,
  ReturnOrder = 2,
  Upsell = 3,
  CancelSubscription = 4,
  CancelTrial = 5,
  Other = 6,
  AdjustShippingFrequency = 7,
  PauseSubscription = 8,
  ReviseOrder = 9,
  ReactivateSubscription = 10,
  CancelSubscriptionHold = 11,
  CancelTrialHold = 12,
  OfferDetails = 13,
  ReturnSubscription = 14,
  ReturnTrial = 15,
  ChangeVariant = 16,
  ChangeSubscriptionProduct = 17,
  ChangeSubscriptionVariant = 18,
  SkipSubscription = 19,
  CancelRebillHold = 20,
  ShipmentTracking = 21,
  ItemStatus = 22,
  FixedProductRevision = 23,
  BillNow = 24,
  RebillItem = 25,
  ShippingProcessing = 26,
  ReturnPending = 27,
  ExtendTrial = 28,
  AdjustTrialFrequency = 29,
  BillTrialNow = 30,
  Resolve = 31,
  ResolveSubscription = 32,
}

export const ResolveOfferIntents = [
  OfferIntents.Resolve,
  OfferIntents.ResolveSubscription,
];

export const LinkOfferIntents = [
  OfferIntents.CancelOrder,
  OfferIntents.ReturnOrder,
  OfferIntents.CancelSubscription,
  OfferIntents.AdjustShippingFrequency,
  OfferIntents.PauseSubscription,
  OfferIntents.ReactivateSubscription,
  OfferIntents.OfferDetails,
  OfferIntents.ChangeVariant,
  OfferIntents.ChangeSubscriptionProduct,
  OfferIntents.ChangeSubscriptionVariant,
  OfferIntents.SkipSubscription,
  OfferIntents.ShipmentTracking,
  OfferIntents.FixedProductRevision,
  OfferIntents.ReviseOrder,
];

export const SubscriptionOfferIntents = [
  OfferIntents.CancelSubscription,
  OfferIntents.AdjustShippingFrequency,
  OfferIntents.PauseSubscription,
  OfferIntents.ReactivateSubscription,
  OfferIntents.CancelSubscriptionHold,
  OfferIntents.ReturnSubscription,
  OfferIntents.ChangeSubscriptionProduct,
  OfferIntents.ChangeSubscriptionVariant,
  OfferIntents.SkipSubscription,
  OfferIntents.CancelRebillHold,
];

export const OfferStages = {};
OfferStages[OfferIntents.Legacy] = 'Legacy';
OfferStages[OfferIntents.CancelOrder] = 'One-Time Order Pending';
OfferStages[OfferIntents.CancelSubscriptionHold] = 'Pending Initial Order (Subscription)';
OfferStages[OfferIntents.CancelTrialHold] = 'Trial Hold';
OfferStages[OfferIntents.CancelSubscription] = 'Subscription Active';
OfferStages[OfferIntents.CancelTrial] = 'Trial Active';
OfferStages[OfferIntents.ReturnOrder] = 'Return One-Time Order';
OfferStages[OfferIntents.ReturnSubscription] = 'Return Subscription Order';
OfferStages[OfferIntents.ReturnTrial] = 'Return Trial Order';
OfferStages[OfferIntents.CancelRebillHold] = 'Pending Rebill Order (Subscription)';
OfferStages[OfferIntents.Resolve] = 'Resolve Orders';
OfferStages[OfferIntents.ResolveSubscription] = 'Resolve Subscription';

export const abbrOfferStageLabel = {};
abbrOfferStageLabel[OfferIntents.Legacy] = 'Legacy';
abbrOfferStageLabel[OfferIntents.CancelOrder] = 'One-Time';
abbrOfferStageLabel[OfferIntents.CancelSubscriptionHold] = 'Pending Initial';
abbrOfferStageLabel[OfferIntents.CancelRebillHold] = 'Pending Rebill';
abbrOfferStageLabel[OfferIntents.ReturnOrder] = 'One-Time';
abbrOfferStageLabel[OfferIntents.ReturnSubscription] = 'Subscription';
abbrOfferStageLabel[OfferIntents.ReturnTrial] = 'Trial';
abbrOfferStageLabel[OfferIntents.CancelTrialHold] = 'Pending Trial';
abbrOfferStageLabel[OfferIntents.CancelSubscription] = 'Active Subscription';
abbrOfferStageLabel[OfferIntents.CancelTrial] = 'Active Trial';

export const OfferIntentLabels = {};
OfferIntentLabels[OfferIntents.Legacy] = 'Legacy';
OfferIntentLabels[OfferIntents.CancelOrder] = 'Cancel One-Time Order';
OfferIntentLabels[OfferIntents.CancelSubscriptionHold] = 'Cancel Subscription Order';
OfferIntentLabels[OfferIntents.CancelTrialHold] = 'Cancel Trial Order';
OfferIntentLabels[OfferIntents.CancelSubscription] = 'Cancel Active Subscription';
OfferIntentLabels[OfferIntents.CancelTrial] = 'Cancel Active Trial';
OfferIntentLabels[OfferIntents.ReturnOrder] = 'Return One-Time Order';
OfferIntentLabels[OfferIntents.ReturnSubscription] = 'Return Subscription Order';
OfferIntentLabels[OfferIntents.ReturnTrial] = 'Return Trial Order';
OfferIntentLabels[OfferIntents.AdjustShippingFrequency] = 'Adjust Shipping Frequency';
OfferIntentLabels[OfferIntents.ReactivateSubscription] = 'Reactivate Subscription';
OfferIntentLabels[OfferIntents.OfferDetails] = 'Offer Details';
OfferIntentLabels[OfferIntents.PauseSubscription] = 'Pause Subscription';
OfferIntentLabels[OfferIntents.ReviseOrder] = 'Swap Products';
OfferIntentLabels[OfferIntents.ChangeVariant] = 'Change Order Variant';
OfferIntentLabels[OfferIntents.ChangeSubscriptionProduct] = 'Change Subscription Product';
OfferIntentLabels[OfferIntents.ChangeSubscriptionVariant] = 'Change Subscription Variant';
OfferIntentLabels[OfferIntents.SkipSubscription] = 'Skip Next Shipment';
OfferIntentLabels[OfferIntents.CancelRebillHold] = 'Cancel Rebill Order';
OfferIntentLabels[OfferIntents.ShipmentTracking] = 'Shipment Tracking';
OfferIntentLabels[OfferIntents.Upsell] = 'Upsell Order';
OfferIntentLabels[OfferIntents.ItemStatus] = 'Order Item Status';
OfferIntentLabels[OfferIntents.FixedProductRevision] = 'Fixed Product Swap';
OfferIntentLabels[OfferIntents.BillNow] = 'Bill Now';
OfferIntentLabels[OfferIntents.RebillItem] = 'Rebill Item';
OfferIntentLabels[OfferIntents.ShippingProcessing] = 'Shipping Processing';
OfferIntentLabels[OfferIntents.ReturnPending] = 'Return Pending';
OfferIntentLabels[OfferIntents.ExtendTrial] = 'Extend Trial';
OfferIntentLabels[OfferIntents.AdjustTrialFrequency] = 'Adjust Trial Frequency';
OfferIntentLabels[OfferIntents.BillTrialNow] = 'Bill Trial Now';
OfferIntentLabels[OfferIntents.Resolve] = 'Resolve';
OfferIntentLabels[OfferIntents.ResolveSubscription] = 'Resolve Subscription';

export const BooleanFilterValue = {
  Yes: 1,
  No: 0,
};

export const JourneyIntentLabels = {};
JourneyIntentLabels[OfferIntents.Legacy] = 'Legacy';
JourneyIntentLabels[OfferIntents.CancelOrder] = 'Cancel';
JourneyIntentLabels[OfferIntents.ReturnOrder] = 'Return';
JourneyIntentLabels[OfferIntents.CancelSubscription] = 'Cancel Subscription';
JourneyIntentLabels[OfferIntents.AdjustShippingFrequency] = 'Adjust Shipping Frequency';
JourneyIntentLabels[OfferIntents.PauseSubscription] = 'Pause';
JourneyIntentLabels[OfferIntents.SkipSubscription] = 'Skip Subscription';

export function getOfferIntentDescription(intent: OfferIntents): string {
  const descriptions: Partial<Record<OfferIntents, string>> = {
    [OfferIntents.CancelOrder]:
      'Cancels a one-time purchase before it ships. Offer discounts or product swaps to save the sale and reduce churn from single-order customers.',
    [OfferIntents.CancelSubscriptionHold]:
      'Cancels an initial scheduled subscription shipment before it ships. Use incentives for this and future orders to retain the subscriber and protect LTV.',
    [OfferIntents.CancelRebillHold]:
      'Cancels a scheduled rebill subscription shipment before it ships. Use incentives for this and future orders to retain the subscriber and protect LTV.',
    [OfferIntents.CancelTrialHold]:
      'Triggered when a customer cancels a trial order before it ships. Manages the pre-shipment flow specific to trial product cancellations.',
    [OfferIntents.CancelSubscription]:
      'Triggered when a customer tries to cancel an ongoing subscription. Collect reasons and offer retention incentives before completing cancellation.',
    [OfferIntents.CancelTrial]:
      'Used when a customer is mid-trial and wants to cancel. Guides them through ending the trial, with options to retain or extend the experience.',
    [OfferIntents.ReturnOrder]:
      'Handles return requests for shipped one-time purchases. Configure refund, credit, or exchange options for single-sale orders.',
    [OfferIntents.ReturnSubscription]:
      'Handles returns for delivered subscription items. Customers may want to return a shipment without ending their subscription.',
    [OfferIntents.ReturnTrial]:
      'Triggered when a customer returns a trial item after delivery. Sets rules and options for managing trial-based product returns.',
  };

  return descriptions[intent] || '';
}

export enum OfferTypes {
  Legacy = 0,
  Downsell = 1,
  Upsell = 2,
  SingleStep = 3,
  CustomerPortal = 4,
  Support = 5,
  Survey = 6,
}

export const OfferTypeLabels = {
  [OfferTypes.Downsell]: 'retention',
  [OfferTypes.Upsell]: 'upsell',
  [OfferTypes.SingleStep]: 'single-step',
  [OfferTypes.Survey]: 'survey',
  [OfferTypes.Support]: 'support',
  [OfferTypes.CustomerPortal]: 'customer-portal',
};

export enum PathStatus {
  Active = 'Active',
  Inactive = 'Inactive',
  Draft = 'Draft',
}

FunnelSystemInputChildTypes[FunnelInputTypeEnum.CustomerPortalHome] = FunnelInputCustomerPortalInputs;
FunnelSystemInputChildTypes[FunnelInputTypeEnum.CustomerPortalOrders] = FunnelInputCustomerPortalInputs.concat([
  FunnelInputTypeEnum.CustomerPortalOrderDetail,
]);
FunnelSystemInputChildTypes[FunnelInputTypeEnum.CustomerPortalSubscriptions] = FunnelInputCustomerPortalInputs.concat([
  FunnelInputTypeEnum.CustomerPortalSubscriptionDetail,
]);
FunnelSystemInputChildTypes[FunnelInputTypeEnum.CustomerPortalPersonalInfo] = FunnelInputCustomerPortalInputs;
FunnelSystemInputChildTypes[FunnelInputTypeEnum.CustomerPortalOrderDetail] = FunnelInputCustomerPortalInputs.concat([
  FunnelInputTypeEnum.Item,
]);
FunnelSystemInputChildTypes[FunnelInputTypeEnum.CustomerPortalSubscriptionDetail] = FunnelInputCustomerPortalInputs.concat([
  FunnelInputTypeEnum.SubscriptionItem,
]);

const FunnelInputTypeGroups = [
  [FunnelInputTypeEnum.Radio, FunnelInputTypeEnum.RadioSelect, FunnelInputTypeEnum.Select],
  [FunnelInputTypeEnum.Item, FunnelInputTypeEnum.ActiveItem, FunnelInputTypeEnum.SubscriptionItem],
  [FunnelInputTypeEnum.BillingAddress, FunnelInputTypeEnum.ShippingAddress],
];

export function getFunnelInputTypeGroup(type: FunnelInputTypeEnum) {
  return FunnelInputTypeGroups.find((group) => group.indexOf(type) !== -1) || [type];
}

export function getFunnelItemTypeLabel(
  stepType: FunnelInputTypeEnum,
  inputType: FunnelInputTypeEnum,
  currentLabel?: string
) {
  const defaultSystemLabel = currentLabel || 'Continue';
  let label = currentLabel || '';

  if (FunnelInputSystemTypes.indexOf(stepType) !== -1) {
    switch (stepType) {
      case FunnelInputTypeEnum.BillingAddress:
      case FunnelInputTypeEnum.ShippingAddress:
      case FunnelInputTypeEnum.PaymentInfo:
        if (inputType === stepType) {
          label = currentLabel || 'Save';
        } else {
          label = FunnelInputTypeLabels[inputType];
        }
        break;

      case FunnelInputTypeEnum.ActiveItem:
      case FunnelInputTypeEnum.Item:
      case FunnelInputTypeEnum.RebillItem:
      case FunnelInputTypeEnum.SubscriptionItem:
        label =
          inputType === stepType
            ? defaultSystemLabel
            : inputType === FunnelInputTypeEnum.NoActiveItems
              ? 'No Items Found'
              : FunnelInputTypeLabels[inputType];
        break;

      case FunnelInputTypeEnum.ContactCallCenter:
        label = inputType === stepType ? defaultSystemLabel : 'Call Center Closed';
        break;

      case FunnelInputTypeEnum.RepeatActionHoldoff:
        label = inputType === stepType ? 'Repeating Action' : 'No Repeating Action';
        break;

      case FunnelInputTypeEnum.SelectCampaign:
        label = inputType === stepType ? defaultSystemLabel : 'No Brands Found';
        break;

      case FunnelInputTypeEnum.MatchCustomer:
        label = inputType === stepType ? defaultSystemLabel : 'No Customer Found';
        break;

      case FunnelInputTypeEnum.Checkout:
        if (inputType === stepType) {
          label = currentLabel || 'Process Order';
        }
        break;

      case FunnelInputTypeEnum.EnterTroubleshooter:
        switch (inputType) {
          case FunnelInputTypeEnum.EnterTroubleshooter:
            label = 'Troubleshooter Passed';
            break;

          case FunnelInputTypeEnum.AltChildExit:
            label = 'Troubleshooter Failed';
            break;

          case FunnelInputTypeEnum.PathNotFound:
            label = 'No Troubleshooter Found';
            break;
        }
        break;

      case FunnelInputTypeEnum.EnterProductFunnel:
        switch (inputType) {
          case FunnelInputTypeEnum.EnterProductFunnel:
            label = 'Path Exit';
            break;

          case FunnelInputTypeEnum.AltChildExit:
            label = 'Alternate Path Exit';
            break;

          case FunnelInputTypeEnum.PathNotFound:
            label = 'No Path Found';
            break;
        }
        break;

      case FunnelInputTypeEnum.InputActionStatus:
        switch (inputType) {
          case FunnelInputTypeEnum.ShipmentReturnLabel:
          case FunnelInputTypeEnum.ShipmentReturnQRCode:
            if (!label) {
              label = FunnelInputTypeLabels[inputType];
            }
            break;

          default:
            label = inputType === stepType ? defaultSystemLabel : FunnelInputTypeLabels[inputType];
            break;
        }
        break;

      default:
        label = inputType === stepType ? defaultSystemLabel : FunnelInputTypeLabels[inputType];
        break;
    }
  } else if (FunnelInputCustomerPortalTypes.indexOf(stepType) > -1) {
    if (inputType === FunnelInputTypeEnum.CustomerPortalOrders) {
      label = currentLabel || 'Orders';
    } else if (inputType === FunnelInputTypeEnum.CustomerPortalSubscriptions) {
      label = currentLabel || 'Subscriptions';
    } else {
      label = currentLabel || FunnelInputTypeLabels[inputType].replace('Customer Portal ', '');
    }
  }

  return label;
}

export enum FunnelItemStatus {
  InActive = -1,
  SubscriptionActive = 0,
  SubscriptionInTrial = 1,
  ShippingHold = 2,
  ShippingProcessing = 3,
  OneTimePurchaseShipped = 4,
  OneTimePurchaseInTrial = 5,
  SubscriptionHold = 6,
  InTrialHold = 7,
  RebillHold = 8,
  RebillActive = 9,
  SubscriptionStopped = 10,
  RecycleFailed = 11,
  InTrialRebillHold = 12,
  RMAPending = 13,
}

export const AvailableFunnelItemStatuses = [
  { id: FunnelItemStatus.SubscriptionActive, text: 'Subscription Active' },
  { id: FunnelItemStatus.SubscriptionInTrial, text: 'Subscription In Trial' },
  { id: FunnelItemStatus.OneTimePurchaseShipped, text: 'One-Time Purchase Shipped' },
  { id: FunnelItemStatus.OneTimePurchaseInTrial, text: 'One-Time Purchase In Trial' },
  { id: FunnelItemStatus.ShippingProcessing, text: 'Shipping Processing' },
  { id: FunnelItemStatus.ShippingHold, text: 'Shipping Hold' },
  { id: FunnelItemStatus.SubscriptionHold, text: 'Subscription Hold' },
  { id: FunnelItemStatus.InTrialHold, text: 'In Trial Hold' },
  { id: FunnelItemStatus.InActive, text: 'Inactive' },
  { id: FunnelItemStatus.RebillHold, text: 'Rebill Hold' },
  { id: FunnelItemStatus.RebillActive, text: 'Rebill Active' },
  { id: FunnelItemStatus.SubscriptionStopped, text: 'Subscription Stopped' },
  { id: FunnelItemStatus.RecycleFailed, text: 'Recycle Failed' },
  { id: FunnelItemStatus.InTrialRebillHold, text: 'In Trial Rebill Hold' },
  { id: FunnelItemStatus.RMAPending, text: 'Return Pending' },
];

export const AvailableActionHoldoffs = [
  { id: SessionActionResult.Downsell, text: 'Refund' },
  { id: SessionActionResult.ReturnExtension, text: 'Return Extension' },
  { id: SessionActionResult.Revision, text: 'Revision' },
  { id: SessionActionResult.SubscriptionAdjust, text: 'Subscription Adjustment' },
  { id: SessionActionResult.SubscriptionDownsell, text: 'Subscription Discount' },
  { id: SessionActionResult.SubscriptionPause, text: 'Subscription Pause' },
  { id: SessionActionResult.TrialExtension, text: 'Trial Extension' },
  { id: SessionActionResult.Upsell, text: 'Upsell' },
];

export const AvailableActionHoldOffIntents = [
  { id: OfferIntents.ReviseOrder, text: 'Revise Item' },
  { id: OfferIntents.AdjustShippingFrequency, text: 'Change Item Interval' },
  { id: OfferIntents.PauseSubscription, text: 'Pause Subscription' },
  { id: OfferIntents.SkipSubscription, text: 'Skip Subscription' },
  { id: OfferIntents.ReactivateSubscription, text: 'Reactivate Subscription' },
  { id: OfferIntents.CancelOrder, text: 'Cancel Item' },
  { id: OfferIntents.CancelSubscription, text: 'Cancel Subscription' },
  { id: OfferIntents.ReturnOrder, text: 'Return Item' },
  { id: OfferIntents.ReturnSubscription, text: 'Return Subscription Item' },
  { id: OfferIntents.ReturnTrial, text: 'Return Trial' },
  { id: OfferIntents.CancelTrial, text: 'Cancel Trial' },
  { id: OfferIntents.CancelTrialHold, text: 'Cancel Trial on Hold' },
  { id: OfferIntents.CancelSubscriptionHold, text: 'Cancel Initial Subscription Order' },
  { id: OfferIntents.CancelRebillHold, text: 'Cancel Rebill Subscription Order' },
  { id: OfferIntents.ChangeVariant, text: 'Change Item Variant' },
  { id: OfferIntents.ChangeSubscriptionProduct, text: 'Change Subscription Product' },
  { id: OfferIntents.ChangeSubscriptionVariant, text: 'Change Subscription Variant' },
];

export enum FunnelFulfillmentType {
  Shippable = 1,
  NotShippable = 2,
}

export const FunnelFulfillmentTypeLabels = {
  [FunnelFulfillmentType.Shippable]: 'Shippable',
  [FunnelFulfillmentType.NotShippable]: 'Non-Shippable',
};

interface BaseFunnelInput extends BaseModel {
  label: string;
  help: string;
  next_step: string | number;
  matched_step_key: string;
  type: FunnelInputTypeEnum;
  category: StepCategory;
  product_funnels: string[] | number[];
  step: string | number;
  builder_id?: string | number;
  hide_if_invalid: boolean;
  is_alt_child_exit: boolean;
  has_response_step?: boolean;
  troubleshooter_enabled?: boolean;
  autopick_next_step: string | number;
}

export interface FunnelInput extends BaseFunnelInput {
  icon: IconFile;
  values: string[];
  actions: Action[];
  classes: string[];
  button_styles?: StepButtonStyles;
  mark_hold_status_save: boolean;
  mark_subscription_status_save: boolean;
  mark_rma_status_save: boolean;
  enabled?: boolean;
}

export interface FunnelStepGroup extends BaseModel {
  name: string;
  classes: string[];
  lifeline: string | number;
  lifeline_enabled: boolean;
  error_lifeline: string | number;
  exit_lifeline: string | number;
}

interface BaseFunnelStep extends BaseModel {
  name: string;
  slug: string;
  label: string;
  help: string;
  next_step: string | number; // only used as a default when creating system steps
  voice_prompt: string;
  voice_file: AudioFile;
  sms_prompt: string;
  num_digits: number;
  forward_to_call_center: boolean;
  call_center_phone: string;
  allow_undo: boolean;
  show_step_link: boolean;
  type: FunnelInputTypeEnum;
  category: StepCategory;
  faq_topic: string | number | null;
  banner: string;
  subtitle: string;
  content: string;
  style: string;
  lifeline: string | number;
  lifeline_enabled: boolean;
  group: string | number;
  contact_type: ContactType;
  enhanced_mode: boolean;
  show_selected_product: boolean;
  enhanced_content: string;
  call_center_sip_uri: string;
  call_center_sip_secure: boolean;
  call_center_sip_refer: boolean;
  call_center_sip_username: string;
  call_center_sip_password: string;
  call_center_sip_region: SipRegion;
  fail_if_any_input_invalid: boolean;
  invalid_message: string;
  error_lifeline: string | number;
  exit_lifeline: string | number;
  hide_status: boolean;
  enhanced_content_components?: string;
  mobile_icon_button_type?: number;
  use_bootstrap_spacing?: boolean;
  display_id: number;
  contact_email: string;
}

export enum FunnelStepMobileIconButtonType {
  TILE = 1,
  BOX = 2,
}

export let FunnelStepMobileIconButtonTypeLabels = {};
FunnelStepMobileIconButtonTypeLabels[FunnelStepMobileIconButtonType.TILE] = 'Tile - Buttons';
FunnelStepMobileIconButtonTypeLabels[FunnelStepMobileIconButtonType.BOX] = 'Box - Buttons';

export function getFunnelStepMobileIconButtonTypeOptions() {
  let items = [];

  Object.keys(FunnelStepMobileIconButtonTypeLabels).forEach((key) => {
    items.push({ value: key, text: FunnelStepMobileIconButtonTypeLabels[key] });
  });

  return items;
}

export enum StepStaticDataImageType {
  None = 0,
  Product = 1,
  Tagged = 2,
  Gallery = 3,
  Fixed = 4,
  Embed = 5,
}

export interface StepStaticData {
  id?: number | null;
  step?: number | null;
  image: Image | null;
  image_tablet: Image | null;
  image_mobile: Image | null;
  embed_code: number | null;
  image_type: StepStaticDataImageType;
  is_link_internal?: boolean;
  link?: string;
}

export interface StepButtonStyles {
  label?: string;
  style: string;
  style_type: StepButtonShapes;
  control_type: StepButtonControlTypes;
}

export interface StepExternalButtonStyles {
  confirm: StepButtonStyles;
}

export interface FunnelStep extends BaseFunnelStep {
  funnel: string | number;
  is_first_step: boolean;
  inputs: FunnelInput[];
  item_statuses: FunnelItemStatus[];
  campaign_products: string[] | number[];
  classes: string[];
  is_popup: boolean;
  action_holdoffs: SessionActionResult[];
  previous_inputs: string[] | number[];
  template: string | number;
  static_data: StepStaticData;
  button_styles?: StepExternalButtonStyles;
  response_data?: FunnelStep;
  pre_signed_url?: SignedURL;
}

export enum FunnelType {
  Product = 'ProductFunnel',
  Visual = 'VisualFunnel',
  Voice = 'VoiceFunnel',
  SMS = 'SMSFunnel',
  Troubleshooter = 'Troubleshooter',
  Lifeline = 'Lifeline',
  Survey = 'Survey',
  Hybrid = 'HybridCampaignFunnel',
  CustomerPortal = 'CustomerPortal',
}

export const FunnelTypeLabels = {
  ProductFunnel: 'Product',
  VisualFunnel: 'Visual',
  VoiceFunnel: 'Voice',
  SMSFunnel: 'SMS',
  Troubleshooter: 'Troubleshooter',
  Lifeline: 'Lifeline',
  Survey: 'Survey',
  HybridCampaignFunnel: 'Hybrid',
  CustomerPortal: 'Customer Portal',
};

export enum CancelItemsSelection {
  ShowAll = 1,
  ShowNone = 2,
  UpsellsOnly = 3,
  UpsellsOnlyIfUpsell = 4,
}

interface BaseFunnel extends BaseModel {
  resourcetype: FunnelType;
  name: string;
  first_step: string | number;
  is_visual: boolean;
  is_voice: boolean;
  is_sms: boolean;
  is_after_hours: boolean;
  item_statuses: FunnelItemStatus[];
  voice_type: string;
  matched_verify_msg: string;
  unmatched_verify_msg: string;
  match_msg: string;
  matched_welcome_msg: string;
  unmatched_welcome_msg: string;
  thank_you_msg: string;
  exit_survey: string | number;
  is_test: boolean;
  carrier_type: PhoneCarrierType;
  is_modified: boolean;
  cancel_items_selection: CancelItemsSelection;
  return_items_selection: CancelItemsSelection;
  is_public: boolean;
  is_public_default: boolean;
  is_template: boolean;
  session_type: SessionType;
  match_required: boolean;
  audience: string | number;
  side_menu_inputs?: FunnelInputTypeEnum[];
  banner_image_details?: StepStaticData;
  product_funnels?: string[] | number[];
  slug?: string;
  display_id: number;
  use_intents: boolean;
  offer_intent: OfferIntents;
  offer_type: OfferTypes;
}

export interface Funnel extends BaseFunnel {
  steps: FunnelStep[];
  campaign: string | number;
  campaigns: string[] | number[];
  products: string[] | number[];
  children: BaseFunnel[];
  offer_steps?: FunnelStep[];
  is_active?: boolean;
  updated?: string;
  survey?: SurveyStep;
  troubleshooter_passed_response_step?: FunnelStep;
  audience_name?: string;
}

export interface Path extends BaseModel {
  name: string;
  offer_intent: OfferIntents;
  is_modified: boolean;
  last_modified: string;
  steps: { id: number; name: string; display_id: number }[];
  audience: PathAudience;
  display_id: number;
}

export interface GroupedTableData {
  type: string;
  label: string;
  isExpanded: boolean;
  items: any[];
  lastModified: string;
}

export interface PathAudience {
  id: number;
  name: string;
}

export enum PathTableFilters {
  Name,
  Type,
  LastModified,
  DateCreated,
}

export enum PathTableTypes {
  CancelOrder = 'Cancel Order',
  ReturnOrder = 'Return Order',
  CancelSubscription = 'Cancel Subscription',
  CancelTrial = 'Cancel Trial',
}

export enum PathTableStatuses {
  All,
  Active,
  Inactive,
  Draft,
}

export const PathTableStatusLabels = {
  [PathTableStatuses.All]: 'All',
  [PathTableStatuses.Active]: 'Active',
  [PathTableStatuses.Inactive]: 'Inactive',
  [PathTableStatuses.Draft]: 'Draft',
};

export enum PathDateTypes {
  LastModified = 'lastModified',
  DateCreated = 'dateCreated',
}

export interface FunnelNode extends Funnel {
  children: FunnelNode[];
}

export interface FunnelRelationship {
  funnel_id: string | number;
  parent_id: string | number;
}

const defaultEmulatorProduct: CampaignProduct = {
  price: '156.60',
  shipping_price: '9.95',
  retail_price: '176.60',
  restock_fee: '15.00',
  campaign_product_id: '1339',
  offer_terms: '',
  name: 'Pet Food',
} as CampaignProduct;

const defaultEmulatorUpsaleProduct: CampaignProduct = {
  price: '9.95',
  shipping_price: '4.95',
  retail_price: '12.00',
  restock_fee: '15.00',
  campaign_product_id: '1560',
  offer_terms: '',
  name: 'Earpods',
} as CampaignProduct;

export function calculateProductDiscount(product: CampaignProduct, discount: DiscountSetting): number {
  const price = +product.price || 0.000001; // avoid division by zero
  let discountPrice = price;
  let discountFound = false;

  if (
    [DiscountTypeEnum.Index, DiscountTypeEnum.IndexOrFixed, DiscountTypeEnum.IndexOrPercent].indexOf(discount.type) >= 0
  ) {
    if (product.discounts && discount.index < product.discounts.length) {
      discountPrice = product.discounts[discount.index].price;
      discountFound = true;
    }
  }

  if (!discountFound) {
    if ([DiscountTypeEnum.Fixed, DiscountTypeEnum.IndexOrFixed].indexOf(discount.type) >= 0) {
      discountPrice = price - discount.amount;
    } else if (
      [DiscountTypeEnum.Percent, DiscountTypeEnum.IndexOrPercent, DiscountTypeEnum.PercentOfOrder].indexOf(
        discount.type
      ) >= 0
    ) {
      const discountAmount = price * (discount.amount / 100);
      discountPrice = price - discountAmount;
    }
  }

  return Math.max(discountPrice, 0);
}

// funnel variable values are [label, dummy value for emulator, product value for emulator]
export const FunnelVariables = {
  billing_interval_days: ['Billing Interval (in days)', '30', null],
  brand_name: ['Brand Name', 'PetJoy', null],
  campaign_product_id: ['Campaign Product ID', '1339', 'campaign_product_id'],
  customer_id: ['Customer ID', '49613', null],
  'discount|currency': [
    'Discount Amount',
    '39.15',
    (product: CampaignProduct, discount: DiscountSetting) => {
      const price = +product.price || 0.000001; // avoid division by zero
      return price - calculateProductDiscount(product, discount);
    },
  ],
  discount_percent: [
    'Discount Percent from Current Price',
    '25',
    (product: CampaignProduct, discount: DiscountSetting) => {
      const price = +product.price || 0.000001; // avoid division by zero
      const amount = price - calculateProductDiscount(product, discount);
      return Math.round((amount / price) * 100);
    },
  ],
  'discount_price|currency': [
    'Discounted Sale Price',
    '117.45',
    (product: CampaignProduct, discount: DiscountSetting) => {
      return calculateProductDiscount(product, discount);
    },
  ],
  email: ['Customer Email', 'testymctester@gmail.com', null],
  exchange_days: ['Exchange Days Remaining', '45', null],
  first_name: ['Customer First Name', 'John', null],
  full_name: ['Customer Full Name', 'John Doe', null],
  last_name: ['Customer Last Name', 'Doe', null],
  next_bill_date: [
    'Next Bill Date',
    () => {
      moment().add(30, 'days').calendar();
    },
    null,
  ],
  next_bill_date_days: ['Next Bill Date days from today', '30', null],
  offer_name: [
    'Offer Name',
    'Pet Food',
    (product: CampaignProduct, discount: DiscountSetting) => {
      return product.offer_name || product.name;
    },
  ],
  offer_terms: [
    'Offer Terms',
    'The offer details of the product will go here.',
    (product: CampaignProduct, discount: DiscountSetting) => {
      if (product.widgets && product.widgets.length > 0) {
        let termsWidget = product.widgets.find(widget => widget.slug === ContentBlockType.OfferTerms);
        if (termsWidget) {
          return termsWidget.content;
        }
      }

      let baseProduct = product.product;
      if (baseProduct && baseProduct.widgets && baseProduct.widgets.length > 0) {
        let termsWidget = baseProduct.widgets.find(widget => widget.slug === ContentBlockType.OfferTerms);
        if (termsWidget) {
          return termsWidget.content;
        }
      }

      return 'The offer details of the product will go here.'
    }],
  order_create_date: ['Order Create Date', '10/15/2024', null],
  order_id: ['Order ID', 'A135448', null],
  'order_shipping_price|currency': ['Order Shipping Price', '9.95', null],
  'original_price|currency': ['Original Price', '156.60', 'price'],
  'original_shipping_price|currency': ['Original Shipping Price', '9.95', 'shipping_price'],
  phone_number: ['Customer Phone Number', '516-555-1234', null],
  'price|currency': ['Sale Price', '156.60', 'price'],
  product_image: [
    'Product Default Image',
    null,
    (product: CampaignProduct, discount: DiscountSetting) => {
      return getCampaignProductImageHtml(product);
    },
  ],
  product_name: ['Product Name', 'Pet Food', 'name'],
  product_tagged_image: [
    'Product Tagged Image',
    null,
    (product: CampaignProduct, discount: DiscountSetting) => {
      return getCampaignProductImageHtml(product);
    },
  ],
  reactivation_date: [
    'Reactivation Date',
    () => {
      moment().add(30, 'days').calendar();
    },
    null,
  ],
  'rebill_amount|currency': ['Rebill Amount', '56.95', 'price'],
  'refund|currency': [
    'Refund Amount',
    '39.15',
    (product: CampaignProduct, discount: DiscountSetting) => {
      const price = +product.price || 0.000001; // avoid division by zero
      return price - calculateProductDiscount(product, discount);
    },
  ],
  refund_percent: [
    'Refund Percentage',
    '25',
    (product: CampaignProduct, discount: DiscountSetting) => {
      const price = +product.price || 0.000001; // avoid division by zero
      const refund = price - calculateProductDiscount(product, discount);
      return Math.round((refund / price) * 100);
    },
  ],
  'restock_fee|currency': ['Restocking Fee', '15.00', 'restock_fee'],
  'retail_price|currency': [
    'Retail Price',
    '176.60',
    (product: CampaignProduct, discount: DiscountSetting) => {
      return +product.retail_price || +product.price;
    },
  ],
  'retail_unit_price|currency': [
    'Retail Unit Price',
    '176.60',
    (product: CampaignProduct, discount: DiscountSetting) => {
      return +product.retail_price || +product.price;
    },
  ],
  return_by_date: [
    'Return By Date',
    () => {
      moment().add(30, 'days').calendar();
    },
    null,
  ],
  return_days: ['Return Days Remaining', '30', null],
  rma_number: ['RMA Number', 'RMA15466876', null],
  'savings|currency': [
    'Savings',
    '20.00',
    (product: CampaignProduct, discount: DiscountSetting) => {
      const price = +product.price;
      const retail_price = +product.retail_price || price;
      return retail_price > price ? retail_price - price : 0;
    },
  ],
  'shipping_price|currency': ['Sale Shipping Price', '9.95', 'shipping_price'],
  support_email: ['Support Email', 'support@mycompany.com', null],
  support_phone: ['Support Phone Number', '888-555-1234', null],
  total_discount_percent: [
    'Discount Percent from Original Price',
    '25',
    (product: CampaignProduct, discount: DiscountSetting) => {
      const price = +product.price || 0.000001; // avoid division by zero
      const amount = price - calculateProductDiscount(product, discount);
      return Math.round((amount / price) * 100);
    },
  ],
  'total_price|currency': [
    'Sale Total Price (price + shipping)',
    '166.55',
    (product: CampaignProduct, discount: DiscountSetting) => {
      return +product.price + +product.shipping_price;
    },
  ],
  tracking_number: ['Tracking Number', 'U9868574152358', null],
  'unit_price|currency': ['Unit Price', '156.60', 'price'],
  'unit_savings|currency': [
    'Unit Savings',
    '20.00',
    (product: CampaignProduct, discount: DiscountSetting) => {
      const price = +product.price;
      const retail_price = +product.retail_price || price;
      return retail_price > price ? retail_price - price : 0;
    },
  ],
  upsale_image: [
    'Upsale Product Image',
    null,
    (product: CampaignProduct, discount: DiscountSetting) => {
      return getCampaignProductImageHtml(product);
    },
  ],
  upsale_name: ['Upsale Product Name', 'Earpods', 'name'],
  'upsale_price|currency': ['Upsale Product Price', '9.95', 'price'],
  'upsale_shipping_price|currency': ['Upsale Product Shipping Price', '4.95', 'shipping_price'],
  'upsale_total_price|currency': [
    'Upsale Product Total Price',
    '14.90',
    (product: CampaignProduct, discount: DiscountSetting) => {
      return +product.price + +product.shipping_price;
    },
  ],
  upsale_quantity: ['Upsale Product Quantity', '1', null],
};

export const defaultEmulatorDiscount: DiscountSetting = { type: DiscountTypeEnum.Percent, amount: 25, index: 0 };

export function getDiscountSettingFromStep(step: FunnelStep) {
  if (step && step.inputs) {
    for (const input of step.inputs) {
      if (input.actions) {
        for (const action of input.actions) {
          if (action.discount_type) {
            // if action has a discount type, then it uses a discount/refund
            return { type: action.discount_type, amount: action.discount, index: action.discount_index };
          }
        }
      }
    }
  }

  return defaultEmulatorDiscount;
}

export function parseFunnelVariableForEmulator(
  variableName: string,
  product: CampaignProduct,
  discount: DiscountSetting
) {
  const key = variableName.replace(/[{}]/g, '');
  let variable = FunnelVariables[key];
  let value = variableName;
  let parsed = false;

  if (!variable) {
    variable = FunnelVariables[key.replace('-', '_')]
  }

  if (variable) {
    if (variable[2]) {
      if (!product) {
        product = key.startsWith('upsale') ? defaultEmulatorUpsaleProduct : defaultEmulatorProduct;
      }
      const productField = variable[2];

      if (typeof productField === 'function') {
        if (!discount) {
          discount = defaultEmulatorDiscount;
        }
        value = productField(product, discount);
        parsed = true;
      } else if (typeof productField === 'string' && productField in product) {
        value = product[productField] as string;
        parsed = true;
      }
    }

    if (!parsed) {
      const field = variable[1];

      if (typeof field === 'function') {
        value = field();
      } else {
        value = field;
      }

      parsed = true;
    }

    if (parsed) {
      if (key.endsWith('|currency')) {
        value = '$' + formatMoney(value);
      }
    }
  }

  return value;
}

export const WelcomeMessageVariables = {
  first_name: 'Customer First Name',
  last_name: 'Customer Last Name',
};

export function getFunnelPathVariables() {
  let items = [];

  Object.keys(FunnelVariables).forEach((key) => {
    items.push({ value: '{' + key + '}', text: FunnelVariables[key][0] });
  });

  return items.sort((a, b) => a.text.localeCompare(b.text));
}

export interface PathVariableItem {
  value: string;
  text: string;
  image?: WidgetVariable;
}

export function buildPathVariables(
  widgets: WidgetVariable[] = [],
  options: { supportPhone?: boolean; withImage?: boolean } = {}
): PathVariableItem[] {
  const items: PathVariableItem[] = getFunnelPathVariables();

  (widgets || []).forEach((widget: WidgetVariable) => {
    const item: PathVariableItem = { value: '{' + widget.slug + '}', text: widget.name };

    if (options.withImage) {
      item.image = widget;
    }

    items.push(item);
  });

  if (options.supportPhone) {
    items.push({ value: '{support_phone}', text: 'Call Center Phone' });
  }

  return items.sort((a, b): number => {
    if (a.text < b.text) return -1;
    if (a.text > b.text) return 1;
    return 0;
  });
}

export interface FunnelHistory extends BaseFunnel {
  created: string;
}

export interface FunnelStepHistory extends BaseFunnelStep {
  funnel: FunnelHistory;
  created: string;
}

export interface FunnelInputHistory extends BaseFunnelInput {}

export enum FunnelStepSaveType {
  SaveAndStay = 'saveAndStay',
  Save = 'save',
  Cancel = 'cancel',
}

export function getExitLabel(type: FunnelType) {
  let label = 'Exit path (normal exit)';

  switch (type) {
    case FunnelType.Troubleshooter:
      label = 'Exit troubleshooter (pass)';
      break;

    case FunnelType.Lifeline:
      label = 'Accept lifeline and end session';
      break;
  }

  return label;
}

export function getAlternateExitLabel(type: FunnelType) {
  let label = 'Exit path (alternate exit)';

  switch (type) {
    case FunnelType.Troubleshooter:
      label = 'Exit troubleshooter (fail)';
      break;

    case FunnelType.Lifeline:
      label = 'Close lifeline and continue session';
      break;
  }

  return label;
}

export enum CustomStepCategoryEnum {
  // custom step categories
  Custom = 2000,
  Reasons = 2001,
  OrderOptions = 2002,
  DiscountFutureOrder = 2003,
  RefundOrder = 2004,
  ReturnOrderRMA = 2005,
  UpsellOrder = 2006,
  PauseSub = 2007,
  OfferDetails = 2008,
  ReviseOrder = 2009,
  NeedMoreHelp = 2010,
  AdjustShippingFrequency = 2011,
  ReturnPolicy = 2012,
  RefundWithDiscount = 2013,
  DiscountWithAdjustShippingFrequency = 2014,
  ReActivateSubscription = 2015,
  ReActivateSubscriptionWithDiscount = 2016,
  Survey = 2017,
  FixedAmountDownsell = 2018,
  PercentageDownsell = 2019,
  LossAversion = 2020,
  TimeForFree = 2021,
  ListLossAversion = 2022,
  Testimonial = 2023,
  ExtendTrial = 2024,
  DiscountedUpsell = 2025,
  SpecialOffer = 2026,
  CancelOrderSurvey = 2027,
  ReturnSurvey = 2028,
  Feedback = 2029,
  ScheduleSupport = 2030,
  LifelineStep = 2031,
  Handoff = 2032,
  PauseSurvey = 2033,
  CancelTrialSurvey = 2034,
  CancelSubSurvey = 2035,
  RMA = 2036,
  ChangeVariant = 2037,
  SkipSubscription = 2038,
  Unknown = 2039,
  AdjustTrialFrequency = 2040,
  BillTrialNow = 2041,
  ResolveSurvey = 2042,
}

export type StepCategory = FunnelInputTypeEnum | CustomStepCategoryEnum;
export let StepCategoryLabels = Object.assign({}, FunnelInputTypeLabels);
StepCategoryLabels[CustomStepCategoryEnum.ResolveSurvey] = 'Resolve Survey';
StepCategoryLabels[CustomStepCategoryEnum.Custom] = 'Custom';
StepCategoryLabels[CustomStepCategoryEnum.Reasons] = 'Reasons';
StepCategoryLabels[CustomStepCategoryEnum.OrderOptions] = 'Order Options';
StepCategoryLabels[CustomStepCategoryEnum.DiscountFutureOrder] = 'Discount Offer';
StepCategoryLabels[CustomStepCategoryEnum.RefundOrder] = 'Partial Refund';
StepCategoryLabels[CustomStepCategoryEnum.ReturnOrderRMA] = 'Return Downsell';
StepCategoryLabels[CustomStepCategoryEnum.UpsellOrder] = 'Upsell';
StepCategoryLabels[CustomStepCategoryEnum.PauseSub] = 'Fixed Pause Period';
StepCategoryLabels[CustomStepCategoryEnum.OfferDetails] = 'Offer Details/Info';
StepCategoryLabels[CustomStepCategoryEnum.ReviseOrder] = 'Bundle Swap';
StepCategoryLabels[CustomStepCategoryEnum.ChangeVariant] = 'Change Variant';
StepCategoryLabels[CustomStepCategoryEnum.NeedMoreHelp] = 'Need More Help';
StepCategoryLabels[CustomStepCategoryEnum.AdjustShippingFrequency] = 'Shipping Frequency Offer';
StepCategoryLabels[FunnelInputTypeEnum.RepeatActionHoldoff] = 'Tagging Customer For Repeating Action';
StepCategoryLabels[FunnelInputTypeEnum.ActiveItem] = 'Item Selection';
StepCategoryLabels[FunnelInputTypeEnum.SubscriptionItem] = 'Item Selection';
StepCategoryLabels[FunnelInputTypeEnum.Item] = 'Item Selection';
StepCategoryLabels[FunnelInputTypeEnum.Checkout] = 'Marketplace';
StepCategoryLabels[FunnelInputTypeEnum.CheckoutConfirm] = 'Marketplace';
StepCategoryLabels[FunnelInputTypeEnum.Product] = 'Marketplace';
StepCategoryLabels[FunnelInputTypeEnum.ContactCallCenter] = 'Life Line Step';
StepCategoryLabels[FunnelInputTypeEnum.EnterProductFunnel] = 'Sub Path';
StepCategoryLabels[FunnelInputTypeEnum.EnterTroubleshooter] = 'Troubleshooter';
StepCategoryLabels[FunnelInputTypeEnum.FAQs] = 'FAQs';
StepCategoryLabels[FunnelInputTypeEnum.ItemStatus] = 'Order Status';
StepCategoryLabels[FunnelInputTypeEnum.BillingAddress] = 'Update Info';
StepCategoryLabels[FunnelInputTypeEnum.ShippingAddress] = 'Update Info';
StepCategoryLabels[FunnelInputTypeEnum.PaymentInfo] = 'Update Info';
StepCategoryLabels[FunnelInputTypeEnum.MatchCustomer] = 'Match and Verify';
StepCategoryLabels[FunnelInputTypeEnum.InputActionStatus] = 'Response Step';
StepCategoryLabels[CustomStepCategoryEnum.ReturnPolicy] = 'Policy/Instruction';
StepCategoryLabels[CustomStepCategoryEnum.RefundWithDiscount] = 'Refund With Discount';
StepCategoryLabels[CustomStepCategoryEnum.DiscountWithAdjustShippingFrequency] =
  'Discount with Adjust Shipping Frequency';
StepCategoryLabels[CustomStepCategoryEnum.ReActivateSubscription] = 'Reactivate Subscription';
StepCategoryLabels[CustomStepCategoryEnum.ReActivateSubscriptionWithDiscount] = 'Reactivate Offer';
StepCategoryLabels[CustomStepCategoryEnum.Survey] = 'Basic Survey';
StepCategoryLabels[CustomStepCategoryEnum.CancelOrderSurvey] = 'Cancel Order Survey';
StepCategoryLabels[CustomStepCategoryEnum.CancelSubSurvey] = 'Cancel Subscription Survey';
StepCategoryLabels[CustomStepCategoryEnum.CancelTrialSurvey] = 'Cancel Trial Survey';
StepCategoryLabels[CustomStepCategoryEnum.ReturnSurvey] = 'Return Order Survey';
StepCategoryLabels[CustomStepCategoryEnum.PercentageDownsell] = 'Percentage Downsell';
StepCategoryLabels[CustomStepCategoryEnum.FixedAmountDownsell] = 'Fixed Amount Downsell';
StepCategoryLabels[CustomStepCategoryEnum.PauseSurvey] = 'Pause Subscription (Flexible)';
StepCategoryLabels[CustomStepCategoryEnum.ExtendTrial] = 'Extend Trial';
StepCategoryLabels[CustomStepCategoryEnum.LossAversion] = 'Loss Aversion';
StepCategoryLabels[CustomStepCategoryEnum.TimeForFree] = 'Time for Free';
StepCategoryLabels[CustomStepCategoryEnum.ListLossAversion] = 'List Loss Aversion';
StepCategoryLabels[CustomStepCategoryEnum.Testimonial] = 'Testimonial';
StepCategoryLabels[CustomStepCategoryEnum.SpecialOffer] = 'Special Offer';
StepCategoryLabels[CustomStepCategoryEnum.Feedback] = 'Feedback';
StepCategoryLabels[CustomStepCategoryEnum.ScheduleSupport] = 'Schedule Support';
StepCategoryLabels[CustomStepCategoryEnum.LifelineStep] = 'Lifeline';
StepCategoryLabels[CustomStepCategoryEnum.Handoff] = 'Handoff';
StepCategoryLabels[CustomStepCategoryEnum.PauseSurvey] = 'Pause Survey';
StepCategoryLabels[CustomStepCategoryEnum.CancelTrialSurvey] = 'Cancel Trial Survey';
StepCategoryLabels[CustomStepCategoryEnum.CancelSubSurvey] = 'Cancel Subscription Survey';
StepCategoryLabels[CustomStepCategoryEnum.RMA] = 'Return';
StepCategoryLabels[CustomStepCategoryEnum.ChangeVariant] = 'Change Variant';
StepCategoryLabels[CustomStepCategoryEnum.SkipSubscription] = 'Skip Subscription';
StepCategoryLabels[CustomStepCategoryEnum.DiscountedUpsell] = 'Discounted Upsell';
StepCategoryLabels[CustomStepCategoryEnum.AdjustTrialFrequency] = 'Adjust Trial Frequency';
StepCategoryLabels[CustomStepCategoryEnum.BillTrialNow] = 'Bill Trial Now';
StepCategoryLabels[CustomStepCategoryEnum.Unknown] = 'Unknown';

export enum PathNewStepSelection {
  Survey = 0,
  DownsellOffer = 1,
  UpsellOffer = 2,
  Support = 3,
  Troubleshooter = 4,
}

export enum PathSteps {
  Audience = 'Audience',
  Survey = 'Survey',
  Downsell = 'Downsell',
  Upsell = 'Upsell',
  Support = 'Support',
  Troubleshooter = 'Troubleshooter',
}

export enum PathPopup {
  Create = 0,
  Update = 1,
  Duplicate = 2,
  Delete = 3,
  EditDetails = 4,
}

export const PathNewStepSelectionLabels = {};
PathNewStepSelectionLabels[PathNewStepSelection.Survey] = 'Survey';
PathNewStepSelectionLabels[PathNewStepSelection.DownsellOffer] = 'Downsell Offer';
PathNewStepSelectionLabels[PathNewStepSelection.UpsellOffer] = 'Upsell Offer';
PathNewStepSelectionLabels[PathNewStepSelection.Support] = 'Support';
PathNewStepSelectionLabels[PathNewStepSelection.Troubleshooter] = 'Troubleshooter';

export const IntentConfirmationButtonText = {};
IntentConfirmationButtonText[OfferIntents.CancelOrder] = 'cancel';
IntentConfirmationButtonText[OfferIntents.CancelTrial] = 'cancel';
IntentConfirmationButtonText[OfferIntents.CancelSubscription] = 'cancel';
IntentConfirmationButtonText[OfferIntents.CancelTrialHold] = 'cancel';
IntentConfirmationButtonText[OfferIntents.CancelSubscriptionHold] = 'cancel';
IntentConfirmationButtonText[OfferIntents.CancelRebillHold] = 'cancel';
IntentConfirmationButtonText[OfferIntents.ReturnOrder] = 'return';
IntentConfirmationButtonText[OfferIntents.ReturnSubscription] = 'return';
IntentConfirmationButtonText[OfferIntents.ReturnTrial] = 'return';
IntentConfirmationButtonText[OfferIntents.Upsell] = 'upsell';

export enum CustomerPortalSettingType {
  General = 0,
  Order = 1,
  Subscription = 2,
  Payment = 3,
  Return = 4,
  Lifeline = 5,
}

export interface SettingsPreviewContent {
  heading: string;
  imageSrc: string;
  imageHeight: number;
  bottom: string;
}

export const ConfirmationStepActions = {
  [OfferIntents.CancelOrder]: [ActionType.Cancel],
  [OfferIntents.ReturnOrder]: [ActionType.RMA],
  [OfferIntents.CancelSubscription]: [ActionType.CancelSubscription],
  [OfferIntents.CancelTrial]: [ActionType.RMA, ActionType.BillingCycle],
  [OfferIntents.CancelSubscriptionHold]: [ActionType.Cancel],
  [OfferIntents.CancelRebillHold]: [ActionType.Cancel],
  [OfferIntents.CancelTrialHold]: [ActionType.Cancel],
  [OfferIntents.ReturnSubscription]: [ActionType.RMA, ActionType.CancelSubscription],
  [OfferIntents.ReturnTrial]: [ActionType.RMA, ActionType.BillingCycle],
};

export const CustomerPortalSettingHints = {
  general: {
    name: 'Enter the name for this support path.',
    welcomeMessage: 'Set a personalized greeting for returning customers. (default text already displayed)',
    menuOptions: 'Select which menu options customers see on the portal’s main page.',
    lifeline:
      'Configure support options that activate during customer interactions to assist them at critical moments.',
    generalLifeline: 'Choose a lifeline to guide customers needing extra help.',
    errorLifeline: 'Select a lifeline for situations when errors occur in the session.',
    sessionExitAlert:
      'Select a support option for customers attempting to leave the support portal during their session.',
    searchAssistanceLifeline: 'Low sentiment trigger → negative, upset, critical',
    idleTimeoutDuration: 'Enter idle time (in seconds) before a lifeline appears for inactive sessions.',
    undoStepsLimit: 'Set the number of back steps a customer can take before a lifeline appears.',
    unmatchedLimit: 'Set the number of failed customer match attempts before a lifeline appears.',
    unmatchedLifeline:
      'Select a lifeline for failed customer match.  If none is selected, the general lifeline will be used.',
    subscriptionLifeline:
      'Select a lifeline to show when a customer attempts to cancel a subscription before the minimum days to cancel.',
  },
  order: {
    orderCancellation: 'Allow customers to cancel their orders in the portal.',
    trialCancellation: 'Allow customers to cancel a trial order in the portal.',
    editPaymentMethod: 'Allow customers to edit their payment method in the portal.',
    editShippingAddress: 'Allow customers to edit their shipping address in the portal.',
    fulfillmentStatus: 'Show the shipping status of the order (e.g., pending, shipped, delivered).',
    shipmentTracking: 'Choose where to show shipment tracking (order list or details page).',
    transactionStatus: 'Select where to display transaction status (order list or details page).',
    singleItemAutoSelect: 'Automatically select the only item for RMAs and cancellations, skipping item selection.',
    upsellItemSelection: 'Control which additional items customers can select when cancelling or returning an item.',
    cancelItemOptions: 'Control which additional items customers can select when cancelling an item.',
    returnItemOptions: 'Control which additional items customers can select when returning an item.',
    changeProduct: 'Allow customers to swap their product for another one available in the portal.',
    additionalFeesLabel:
      'Enter a label for any additional fees associated with orders. (i.e: Processing Fee, Handling Fee)',
  },
  subscription: {
    subscriptionCancellation: 'Allow customers to cancel their subscriptions through the customer portal.',
    cancellationDelay:
      'Set a restriction on when customers can cancel their subscription (e.g., after the first cycle).',
    subscriptionStatus: 'Show or hide the current status of the subscription (e.g., active, canceled, inactive).',
    paymentMethodEditing: 'Allow customers to update their payment method.',
    editShippingFrequency: 'Allow customers to update their shipping frequency.',
    shippingAddressEditing: 'Allow customers to modify their shipping address.',
    shippingFrequencyEditing: 'Allow customers to change their subscription shipping frequency.',
    pauseSubscription: 'Allow customers to temporarily pause their subscription or skip the next shipment.',
    reviseSubscriptionItems: 'Allow customers to change items in their subscription for unshipped cycles.',
    nextShipmentDateVisibility: 'Show or hide the date of the next scheduled shipment.',
    reactivateSubscription: 'Allow customers to reactivate their canceled subscription.',
    cancelTrial: 'Allow customers to cancel their trial subscription.',
    changeProduct: 'Allow customers to change the product in their subscription.',
  },
  payment: {
    showOrderPrice: 'Select where to display the order price (order list or details page).',
    taxes: 'Choose to show or hide tax amounts in the support portal.',
    shippingFee: 'Choose to show or hide shipping fees in the support portal.',
    paymentStatus: 'Select where to show the payment status (order list or details page).',
  },
  paths: {
    assignPaths: 'Choose your action paths for this customer portal.',
  },
  actionCooldown: {
    repeatedCrmAction:
      'Enter the default number of hours to holdoff any repeating CRM refund, discount, upsell or exchangeable action. Enter 0 for no default holdoff.',
    cooldownDesc:
      'Prevent customers from repeating the same request too soon. Set a cooldown period to temporarily stop repeated actions like cancellations, refunds, or returns within a chosen timeframe.',
    intent: 'Choose the type of customer request that triggers the cooldown.',
    action: 'Select the specific action you want to delay if a customer has already performed it.',
    delay: 'Set how long to hold off repeated actions after they are triggered.',
  },
};

export function generateStep(
  type: FunnelInputTypeEnum,
  funnel: Funnel,
  category: StepCategory = CustomStepCategoryEnum.Custom
) {
  const step = {
    name: FunnelInputTypeLabels[type] + ' Step ' + category,
    type: type,
    category: category,
    funnel: funnel ? funnel.id : null,
  } as FunnelStep;

  return step;
}

export enum PopupCloseSetting {
  NoClose = 1,
  Undo = 2,
  FirstInput = 3,
  LastInput = 4,
  UndoOrFirstInput = 5,
  UndoOrLastInput = 6,
}

export const PopupCloseSettingLabels = {};
PopupCloseSettingLabels[PopupCloseSetting.NoClose] = 'Hide close icon';
PopupCloseSettingLabels[PopupCloseSetting.Undo] = 'Go back to previous step (only if possible)';
PopupCloseSettingLabels[PopupCloseSetting.FirstInput] = 'Select first input (i.e. Accept Button)';
PopupCloseSettingLabels[PopupCloseSetting.LastInput] = 'Select last input (i.e. Reject Button)';
PopupCloseSettingLabels[PopupCloseSetting.UndoOrFirstInput] = 'Go back or select first input if unable to go back';
PopupCloseSettingLabels[PopupCloseSetting.UndoOrLastInput] = 'Go back or select last input if unable to go back';

export const PopupCloseSettingStatusLabels = {};
PopupCloseSettingStatusLabels[PopupCloseSetting.NoClose] = 'Hide close icon';
PopupCloseSettingStatusLabels[PopupCloseSetting.Undo] = 'Go back to previous step (only if possible)';
PopupCloseSettingStatusLabels[PopupCloseSetting.FirstInput] = 'Continue to next step';
PopupCloseSettingStatusLabels[PopupCloseSetting.UndoOrFirstInput] = 'Go back or continue if unable to go back';

export function isIntentProductPath(funnel: Funnel) {
  return funnel && funnel.resourcetype === FunnelType.Product && funnel.offer_intent !== OfferIntents.Legacy;
}

export const PathStepsDescription = {
  AudienceFilter: {
    tooltip: 'Target a specific group of customers based on order attributes.',
    helpingText:
      'Use this to apply conditions like specific products or purchase history that customers must meet to see this path.',
  },
  Survey: {
    tooltip: 'Ask customers why they’re canceling or returning, using pre-built surveys.',
    helpingText:
      'Attach a survey from your list to understand intent. You can customize responses with different retention offers.',
  },
  RetentionOffer: {
    tooltip: 'Try to retain the customer with discounts, delays, or alternatives based on their reason.',
    helpingText:
      'These are pre-built flows that appear after survey responses or can be shown to all customers in the path.',
  },
  UpsellOffer: {
    tooltip: 'Promote other products or upgrades as an alternative to cancellation or return.',
    helpingText: 'Encourage customers to switch or upgrade instead of leaving shown after survey or retention steps.',
  },
  Troubleshooter: {
    tooltip: 'Help customers fix problems with their order before they proceed to return or cancel.',
    helpingText:
      'Attach a troubleshooter flow that walks customers through resolving issues like missing or broken items.',
  },
};

export interface ReasonCategory extends BaseModel {
  name: string;
  is_global: boolean;
}

export enum DefaultSurveyCategories {
  OrderIssue = 'Order issue',
  FrequencyIssue = 'Frequency issue',
  CostFactor = 'Cost factor',
  UnwantedBuyerRegret = 'Unwanted / buyer regret',
  Other = 'Other',
  ProductIssue = 'Product issue',
  BillingRenewalIssue = 'Billing / renewal issue',
}

export enum DefaultSurveyReasons {
  OrderedWrongItem = 'Ordered Wrong Item',
  TooMuchProduct = 'Too Much Product',
  TooExpensive = 'Too Expensive',
  ChangedMyMind = 'Changed My Mind',
  OtherReason = 'Other Reason',
  NotWorkingForMe = 'Not Working For Me',
  ForgotAboutRenewal = 'Forgot About Renewal',
  WantToDelay = 'Want To Delay',
}

export const StageSurveyMappings = {
  [OfferIntents.CancelOrder]: [
    { reason: DefaultSurveyReasons.OrderedWrongItem, category: DefaultSurveyCategories.OrderIssue },
    { reason: DefaultSurveyReasons.TooMuchProduct, category: DefaultSurveyCategories.FrequencyIssue },
    { reason: DefaultSurveyReasons.TooExpensive, category: DefaultSurveyCategories.CostFactor },
    { reason: DefaultSurveyReasons.ChangedMyMind, category: DefaultSurveyCategories.UnwantedBuyerRegret },
    { reason: DefaultSurveyReasons.OtherReason, category: DefaultSurveyCategories.Other },
  ],
  [OfferIntents.CancelSubscription]: [
    { reason: DefaultSurveyReasons.TooMuchProduct, category: DefaultSurveyCategories.FrequencyIssue },
    { reason: DefaultSurveyReasons.TooExpensive, category: DefaultSurveyCategories.CostFactor },
    { reason: DefaultSurveyReasons.NotWorkingForMe, category: DefaultSurveyCategories.ProductIssue },
    { reason: DefaultSurveyReasons.OtherReason, category: DefaultSurveyCategories.Other },
  ],
  [OfferIntents.CancelSubscriptionHold]: [
    { reason: DefaultSurveyReasons.OrderedWrongItem, category: DefaultSurveyCategories.OrderIssue },
    { reason: DefaultSurveyReasons.TooMuchProduct, category: DefaultSurveyCategories.FrequencyIssue },
    { reason: DefaultSurveyReasons.TooExpensive, category: DefaultSurveyCategories.CostFactor },
    { reason: DefaultSurveyReasons.ChangedMyMind, category: DefaultSurveyCategories.UnwantedBuyerRegret },
  ],
  [OfferIntents.CancelRebillHold]: [
    { reason: DefaultSurveyReasons.TooMuchProduct, category: DefaultSurveyCategories.FrequencyIssue },
    { reason: DefaultSurveyReasons.TooExpensive, category: DefaultSurveyCategories.CostFactor },
    { reason: DefaultSurveyReasons.ForgotAboutRenewal, category: DefaultSurveyCategories.BillingRenewalIssue },
    { reason: DefaultSurveyReasons.NotWorkingForMe, category: DefaultSurveyCategories.ProductIssue },
    { reason: DefaultSurveyReasons.WantToDelay, category: DefaultSurveyCategories.FrequencyIssue },
    { reason: DefaultSurveyReasons.OtherReason, category: DefaultSurveyCategories.Other },
  ],
}
